import { useState, type FormEvent } from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import {
  ArrowLeft, Mail, AlertTriangle, CheckCircle2, ArrowRight, MailCheck,
} from "lucide-react"
import { ApiError, api } from "../../lib/api"
import { SCHOOL } from "../../lib/constants"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import { Alert } from "../../components/ui/Alert"
import { Card, CardContent } from "../../components/ui/Card"
import { ThemeToggle } from "../../components/ui/ThemeToggle"

export default function ForgotPassword() {
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!email) {
      setError("Please enter your email address.")
      return
    }
    setLoading(true)
    try {
      await api.post("/api/auth/forgot-password", { email })
      setSent(true)
      toast.success("Reset link request received.")
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError("Failed to send reset link. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-ink-50 dark:bg-ink-950">
      {/* Top bar */}
      <header className="flex items-center justify-between p-4 md:p-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-600 dark:text-ink-300 hover:text-brand-700 dark:hover:text-brand-400 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to website
        </Link>
        <ThemeToggle />
      </header>

      <main className="flex-1 flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md">
          <Card className="bg-white dark:bg-ink-900">
            <CardContent className="p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-brand-50 dark:bg-brand-900/40 grid place-items-center mx-auto mb-3">
                  {sent ? (
                    <MailCheck className="w-7 h-7 text-brand-700 dark:text-brand-400" />
                  ) : (
                    <Mail className="w-7 h-7 text-brand-700 dark:text-brand-400" />
                  )}
                </div>
                <h1 className="font-display text-2xl font-bold text-ink-900 dark:text-ink-100 mb-1">
                  Forgot Password
                </h1>
                <p className="text-sm text-ink-500 dark:text-ink-400">
                  {sent
                    ? "Check your inbox for the reset link."
                    : "Enter your email and we'll send you a reset link."}
                </p>
              </div>

              {error && (
                <Alert variant="danger" className="mb-4 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </Alert>
              )}

              {sent ? (
                <div className="space-y-4">
                  <Alert variant="success" className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      If an account exists for <strong>{email}</strong>, a reset link has been sent.
                      Please check your inbox and spam folder.
                    </span>
                  </Alert>
                  <div className="rounded-lg border border-ink-200 dark:border-ink-800 bg-ink-50 dark:bg-ink-900/60 p-4">
                    <p className="text-xs text-ink-600 dark:text-ink-400 leading-relaxed">
                      The reset link will expire in <strong>1 hour</strong>. If you don't receive an
                      email within a few minutes, verify the address you entered or contact the
                      administrator at <a href={`mailto:${SCHOOL.email}`} className="text-brand-700 dark:text-brand-400 underline">{SCHOOL.email}</a>.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full bg-white dark:bg-ink-900"
                    onClick={() => {
                      setSent(false)
                      setEmail("")
                    }}
                  >
                    Try a different email
                  </Button>
                  <Link to="/portal/login">
                    <Button variant="ghost" size="lg" className="w-full">
                      <ArrowLeft className="w-4 h-4" />
                      Back to Login
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        className="pl-9 bg-white dark:bg-ink-900"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />
                        Sending link...
                      </>
                    ) : (
                      <>
                        Send Reset Link
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              {!sent && (
                <div className="mt-6 pt-4 border-t border-ink-200 dark:border-ink-800 text-center">
                  <p className="text-sm text-ink-600 dark:text-ink-400">
                    Remembered your password?{" "}
                    <Link to="/portal/login" className="font-semibold text-brand-700 dark:text-brand-400 hover:underline">
                      Back to Login
                    </Link>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  )
}
